import { signOut, fetchAuthSession, getCurrentUser } from "@aws-amplify/auth";
import { LocalStorageHandler } from "@/utils/LocalStorageHandler";
import api from "@/services/api";

const strgHndlr = new LocalStorageHandler();

export default {
  namespaced: true, // Name of namespace exists here '@/store/namespaces'
  state: {
    signingOut: false,
    userInfo: null,
    accessTokenCheckerTimeout: null,
    refreshTokenCheckerTimeout: null,
    latestMoveTimestamp: null,
    userInactivityInterval: null,
  },
  getters: {
    getSigningOut: (state) => {
      return state.signingOut;
    },
    getUserInfo: (state) => {
      return state.userInfo;
    },
    getAccessTokenCheckerTimeout: (state) => {
      return state.accessTokenCheckerTimeout;
    },
    getRefreshTokenCheckerTimeout: (state) => {
      return state.refreshTokenCheckerTimeout;
    },
    getUserInactivityInterval: (state) => {
      return state.userInactivityInterval;
    },
    getLatestMoveTimestamp: (state) => {
      return state.latestMoveTimestamp;
    },
  },
  mutations: {
    setSigningOut: (state, newValue) => {
      state.signingOut = newValue;
    },
    setUserInfo: (state, userInfo) => {
      state.userInfo = userInfo;
    },
    setAccessTokenCheckerTimeout: (state, timeoutId) => {
      state.accessTokenCheckerTimeout = timeoutId;
    },
    setRefreshTokenCheckerTimeout: (state, timeoutId) => {
      state.refreshTokenCheckerTimeout = timeoutId;
    },
    setUserInactivityInterval: (state, intervalId) => {
      state.userInactivityInterval = intervalId;
    },
    setLatestMoveTimestamp: (state, timestamp) => {
      console.log(
        "Setting latest move timestamp in state. Timestamp:",
        timestamp
      );
      state.latestMoveTimestamp = timestamp;
    },
  },
  actions: {
    checkForUserInactivity({ getters, dispatch }) {
      // If the user has made a move within the last 5 minutes, we consider them active and do not log them out
      const latest = getters.getLatestMoveTimestamp;

      if (!latest) {
        console.warn(
          "No latest move timestamp found, unable to check for user inactivity"
        );
        return;
      }

      const now = Date.now();
      const inactivityThreshold = 300000; // 5 minutes in milliseconds

      if (now - latest > inactivityThreshold) {
        console.log(
          "User has been inactive for more than 5 minutes, deactivating session"
        );
        dispatch("deactivateUserSession");
      }
    },
    async handleAccessTokenRefresh({ dispatch }) {
      try {
        console.log("Handling access token refresh. Date.now():", Date.now());
        // Fetch a new session from Amplify, which will include a new access token and its expiration time
        const session = await fetchAuthSession({ forceRefresh: true });

        // Store the new access token expiration time in local storage
        const expTimeInSecs = session.tokens.accessToken?.payload?.exp;
        strgHndlr.storeAccessTokenExpTimestamp(expTimeInSecs);

        const accessTokenStr = session.tokens.accessToken?.toString?.();
        strgHndlr.storeAccessToken(accessTokenStr);

        // Notify the backend to renew the user session record with the new token expiration time
        await api.patch(`/user-sessions`, {
          action: "renew",
        });

        // Re-initialize the access token checker to set up the next timeout based on the new token's expiration time
        dispatch("initAccessTokenChecker");
      } catch (error) {
        console.error("Error refreshing access token:", error);
      }
    },
    initUserInactivityChecker({ commit, dispatch }) {
      // Check every three minutes if user has been active.
      const intervalId = setInterval(() => {
        dispatch("checkForUserInactivity");
      }, 180000);
      commit("setUserInactivityInterval", intervalId);
    },
    initRefreshTokenChecker({ commit, getters, dispatch }) {
      if (getters.getRefreshTokenCheckerTimeout) {
        console.warn(
          "Refresh token checker timeout already exists, curious to know why we're initializing again. Clearing existing timeout and re-initializing."
        );
        clearTimeout(getters.getRefreshTokenCheckerTimeout);
      }

      // Refresh token expiration time is stored in local storage when the session is started. Let's calculate how long until the refresh token expires and set a timeout to log out the user 4 minutes before expiration since we won't be able to refresh the access token after the refresh token expires.
      const expTimestamp = strgHndlr.getRefreshTokenExpTimestamp();

      const nowTimestamp = Date.now();
      const timeUntilExp = expTimestamp * 1000 - nowTimestamp; // Convert exp time to ms and calculate time until expiration
      const bufferTime = 240000; // 4 minutes in milliseconds
      const timeoutDuration = timeUntilExp - bufferTime;

      if (timeoutDuration <= 0) {
        // If the token is already expired, even if it's within the buffer time, we should log out the user since we won't be able to refresh the token
        dispatch("deactivateUserSession");
        return;
      }

      console.log(
        "Setting refresh token timeout. Date.now():",
        Date.now(),
        "timeoutDuration:",
        timeoutDuration
      );
      const timeoutId = setTimeout(() => {
        dispatch("deactivateUserSession");
      }, timeoutDuration);
      commit("setRefreshTokenCheckerTimeout", timeoutId);
    },
    initAccessTokenChecker({ commit, dispatch, getters }) {
      if (getters.getAccessTokenCheckerTimeout) {
        console.warn(
          "Access token checker timeout already exists, curious to know why we're initializing again. Clearing existing timeout and re-initializing."
        );
        clearTimeout(getters.getAccessTokenCheckerTimeout);
      }

      // The access token expiration time is stored in local storage when the session is started and whenever the token is refreshed. Let's calculate how long until the token expires and set a timeout to refresh it 45 seconds before expiration.
      const expTimestamp = strgHndlr.getAccessTokenExpTimestamp();

      const nowTimestamp = Date.now();
      const timeUntilExp = expTimestamp * 1000 - nowTimestamp; // Convert exp time to ms and calculate time until expiration
      const bufferTime = 45000; // 45 seconds in milliseconds
      const timeoutDuration = timeUntilExp - bufferTime;

      if (timeoutDuration < bufferTime * -1) {
        console.error(
          "Access token is expired beyond the buffer time. Unable to refresh."
        );
        return;
      }

      if (timeoutDuration <= 0) {
        // If the token is already expired BUT still within the buffer time, refresh immediately
        dispatch("handleAccessTokenRefresh");
        return;
      }

      console.log(
        "Setting access token refresh timeout. Date.now():",
        Date.now(),
        "timeoutDuration:",
        timeoutDuration
      );
      const timeoutId = setTimeout(() => {
        dispatch("handleAccessTokenRefresh");
      }, timeoutDuration);
      commit("setAccessTokenCheckerTimeout", timeoutId);
    },
    initIntervalsAndTimeouts({ dispatch }) {
      dispatch("initAccessTokenChecker");
      dispatch("initRefreshTokenChecker");
      dispatch("initUserInactivityChecker");
    },
    clearIntervalsAndTimeouts({ getters, commit }) {
      if (getters.getAccessTokenCheckerTimeout) {
        clearTimeout(getters.getAccessTokenCheckerTimeout);
        commit("setAccessTokenCheckerTimeout", null);
      }

      if (getters.getRefreshTokenCheckerTimeout) {
        clearTimeout(getters.getRefreshTokenCheckerTimeout);
        commit("setRefreshTokenCheckerTimeout", null);
      }

      if (getters.getUserInactivityInterval) {
        clearInterval(getters.getUserInactivityInterval);
        commit("setUserInactivityInterval", null);
      }

      // Not a timeout or interval, but we should also clear the latest move timestamp from the state when clearing session-related data to avoid confusion on session status
      commit("setLatestMoveTimestamp", null);
    },
    //endregion
    //region StartUserSession
    async startUserSession({ dispatch }) {
      try {
        // Fetch auth session from Amplify
        const session = await fetchAuthSession();

        // Store the access token in local storage for use in API calls
        const accessTokenStr = session.tokens.accessToken?.toString?.();
        strgHndlr.storeAccessToken(accessTokenStr);

        // If the backend session already exists and is valid for this token, skip creation.
        // This prevents duplicate create attempts on hard refresh while still recovering
        // when no DB session exists.
        let hasActiveBackendSession = false;
        try {
          await api.get("/user-sessions");
          hasActiveBackendSession = true;
        } catch (error) {
          const statusCode = error?.response?.status;
          if (![401, 404, 500].includes(statusCode)) {
            throw error;
          }
        }

        if (!hasActiveBackendSession) {
          // Notify the backend to create a user session record
          await api.post("/user-sessions");
        }

        // Store the access token and refresh token expiration times in local storage to be used by timeouts
        const accessTknExpTimeInSecs = session.tokens.accessToken?.payload?.exp;
        const refreshTknExpTimeInSecs = session.tokens.idToken?.payload?.exp;
        strgHndlr.storeAccessTokenExpTimestamp(accessTknExpTimeInSecs);
        strgHndlr.storeRefreshTokenExpTimestamp(refreshTknExpTimeInSecs);
      } catch (error) {
        console.error("Error starting user session:", error);
        // In case of any error during session initialization, ensure we clean up any stored tokens and session info to avoid inconsistent state
        dispatch("deactivateUserSession");
        // throw new Error(error);
      }
    },
    //endregion
    //region StopUserSession
    async deactivateUserSession({ dispatch, commit }) {
      try {
        console.log("Deactivating user session. Date.now():", Date.now());

        commit("setSigningOut", true);
        // First, deactivate the user session on the backend
        await api.patch(`/user-sessions`, {
          action: "deactivate",
        });
      } catch (error) {
        console.error("Error deactivating user session:", error);
        // throw new Error(error);
      } finally {
        // Regardless of whether we were able to notify the backend, clear intervals and timeouts, sign out from Amplify Auth and clear local storage
        dispatch("clearIntervalsAndTimeouts");
        strgHndlr.resetLocalStorage();
        await signOut();

        commit("setSigningOut", false);
      }
    },
    //endregion
    //region GetUserInfo
    async getUserInfo({ getters, commit }) {
      // If we already have user info in the state, return it to avoid unnecessary API calls
      try {
        if (getters.getUserInfo) return getters.getUserInfo;

        const userDetails = await getCurrentUser();
        const infoToSave = {
          id: userDetails.userId,
          username: userDetails.username,
        };

        commit("setUserInfo", infoToSave);
        return infoToSave;
      } catch (error) {
        console.error("Error fetching user info:", error);
        commit("setUserInfo", null);
        return null;
      }
    },
    //endregion
  },
};
